import { deepmerge } from "@mui/utils";
import { useMemo } from "react";
import { proxy, ref, subscribe, useSnapshot } from "valtio";
import { isDev } from "../../../base/index.js";
const defaultToolbarStoreId = "tidal.toolbar.anonymous.store";
const toolbarStoreCache = proxy({});
function getDefaultState(storeId) {
  return {
    activeView: null,
    meta: {},
    storeId,
    width: 0
  };
}
function getToolbar(storeId = defaultToolbarStoreId) {
  if (!toolbarStoreCache[storeId]) {
    if (storeId === defaultToolbarStoreId) {
      createToolbarStore({ storeId });
    } else {
      throw new Error(`No entry found for storeId "${storeId}".`);
    }
  }
  return toolbarStoreCache[storeId];
}
function createToolbarStore(props) {
  const { storeId = defaultToolbarStoreId, initialState } = props ?? {};
  const state = deepmerge(getDefaultState(storeId), initialState);
  const isDefaultId = storeId === defaultToolbarStoreId;
  if (toolbarStoreCache[storeId]) {
    if (isDev()) {
      const msg = isDefaultId ? "Only one anonymous/unnamed store may exist" : `Duplicate storeId "${storeId}" detected`;
      console.warn(`${msg}. This is a no-op.`);
    }
  } else {
    toolbarStoreCache[storeId] = state;
  }
  return storeId;
}
function getToolbarApi(storeId = defaultToolbarStoreId) {
  const store = getToolbar(storeId);
  const subscribers = [];
  return {
    setActiveView(valueOrFn) {
      if (typeof valueOrFn === "function") {
        store.activeView = valueOrFn(store.activeView);
      } else {
        store.activeView = valueOrFn;
      }
    },
    setMetaTarget(valueOrFn) {
      if (valueOrFn) {
        if (typeof valueOrFn === "function") {
          store.meta = ref({ ...store.meta, target: valueOrFn(store.meta.target) });
        } else if (typeof valueOrFn === "object") {
          store.meta = ref({ ...store.meta, target: valueOrFn });
        }
      } else store.meta = ref({ ...store.meta, target: void 0 });
    },
    setMetaRail(valueOrFn) {
      if (valueOrFn) {
        if (typeof valueOrFn === "function") {
          store.meta = ref({ ...store.meta, rail: valueOrFn(store.meta.rail) });
        } else if (typeof valueOrFn === "object") {
          store.meta = ref({ ...store.meta, rail: valueOrFn });
        }
      } else store.meta = ref({ ...store.meta, rail: void 0 });
    },
    setWidth(valueOrFn) {
      if (typeof valueOrFn === "function") {
        store.width = valueOrFn(store.width);
      } else {
        store.width = valueOrFn;
      }
    },
    remove() {
      subscribers.forEach((unsubscribe) => {
        unsubscribe();
      });
      return delete toolbarStoreCache[storeId];
    },
    subscribe(callback) {
      const unsubscribe = subscribe(toolbarStoreCache[storeId], callback);
      subscribers.push(unsubscribe);
    }
  };
}
function getToolbarState(storeId = defaultToolbarStoreId) {
  const store = getToolbar(storeId);
  return store;
}
function useToolbarState(storeId) {
  const store = getToolbar(storeId);
  return useSnapshot(store);
}
function useToolbarApi(storeId) {
  return useMemo(() => getToolbarApi(storeId), [storeId]);
}
export {
  createToolbarStore,
  getToolbarApi,
  getToolbarState,
  useToolbarApi,
  useToolbarState
};
//# sourceMappingURL=store.js.map
